/**
 * D143's refusal dialog — **an alert, not a confirmation.** One dismiss action, no confirm.
 *
 * A sibling of `Popover` rather than a variant of it, and the shape is the whole reason: that one
 * is a question (`onCancel` + `onConfirm` around an input), this one is an answer. Adding an
 * optional `onConfirm` to `Popover` would make the difference a prop, and a prop is something a
 * later screen can pass — which is exactly how "the last manager may not be removed" would grow a
 * button that removes the last manager. There is no way to proceed from this component because
 * there is no way to proceed from the situation.
 *
 * It renders against the same `.popover*` rules in `global.css`, so the two stay visually one
 * thing, and it inherits the z-index note on `Popover`: render it as a *sibling* of `<Drawer>`,
 * never a child.
 *
 * **Dismiss is reachable three ways** — the button, the backdrop, and `Escape` — because the only
 * outcome available is dismissal, so every instinct a user has for "get me out of this" should
 * work. `role="alertdialog"` (not `dialog`) is what tells a screen reader this interrupts to
 * report something, and it moves focus to the one control.
 */
import { useEffect, useRef, type ReactNode } from 'react'

export function AlertPopover({
  title,
  body,
  dismissLabel,
  onDismiss,
}: {
  readonly title: string
  readonly body: ReactNode
  readonly dismissLabel: string
  onDismiss(): void
}): JSX.Element {
  const dismissRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    dismissRef.current?.focus()
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') onDismiss()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [onDismiss])

  return (
    <div className="popover-backdrop" onClick={onDismiss}>
      <div
        className="popover"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="alert-popover-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="popover-title" id="alert-popover-title">
          {title}
        </h2>
        <p className="popover-body">{body}</p>
        <div className="popover-actions">
          {/*
            `btn-secondary` directly rather than `<SecondaryButton>`: that component does not
            forward a ref, and teaching a shared P6 primitive to do so for this one caller is the
            larger change. The class is exactly what it renders, so the two stay identical.
          */}
          <button ref={dismissRef} type="button" className="btn-secondary" onClick={onDismiss}>
            {dismissLabel}
          </button>
        </div>
      </div>
    </div>
  )
}
